import React from 'react';
import {
  Box,
  Typography,
  Button,
  Link,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { AppTextField } from '@/components/common/AppTextField';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/store/hooks';
import { login } from '@/store/thunks/authThunks';
import { getCurrentEnvironment } from '@/config/environment';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [formData, setFormData] = React.useState({
    email: '',
    password: '',
  });
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [logoutNotice, setLogoutNotice] = React.useState('');

  // Check for logout reason on mount
  React.useEffect(() => {
    const reason = localStorage.getItem('logoutReason');
    if (reason) {
      setLogoutNotice(reason);
      localStorage.removeItem('logoutReason');
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Simple validation
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      // Use real login API
      const response = await dispatch(login(formData.email, formData.password));

      // Skip MFA in dev environment (localhost)
      const isDevEnvironment = getCurrentEnvironment() === 'dev';

      // Check if MFA is required (skip in dev)
      if (response?.mfaRequired && !isDevEnvironment) {
        navigate('/auth/mfa-verify');
        return;
      }

      // Check if MFA setup is needed (first-time, skip in dev)
      if (response?.mfaSetupRequired && !isDevEnvironment) {
        navigate('/auth/mfa-setup');
        return;
      }

      // If MFA is required but we're in dev, show a warning and stop
      // (Backend returned MFA challenge, not user data)
      if ((response?.mfaRequired || response?.mfaSetupRequired) && isDevEnvironment) {
        setError('MFA is enabled on backend. Please disable MFA on local backend or login without MFA-enabled account.');
        setLoading(false);
        return;
      }

      // SECURITY: Check if user has admin role
      const userRole = response?.user?.role?.toLowerCase();
      const allowedRoles = ['admin', 'super_admin', 'manager', 'staff', 'support'];

      if (!allowedRoles.includes(userRole)) {
        setError('Access denied. This dashboard is restricted to administrators only.');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setLoading(false);
        return;
      }

      // Navigate to dashboard on success
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Welcome back!
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sign in to access your dashboard
        </Typography>
      </Box>

      {/* Logout Notice Alert */}
      {logoutNotice && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          onClose={() => setLogoutNotice('')}
        >
          {logoutNotice}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Login Form */}
      <Box component="form" onSubmit={handleSubmit}>
        <AppTextField
          fullWidth
          label="Email or Phone Number"
          name="email"
          type="text"
          value={formData.email}
          onChange={handleChange}
          margin="normal"
          required
          placeholder="Enter your email or phone number"
          autoFocus
        />

        <AppTextField
          fullWidth
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange}
          margin="normal"
          required
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleTogglePasswordVisibility}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  aria-label="toggle password visibility"
                  size="small"
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ mt: 2, mb: 2 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            onClick={() => navigate('/auth/forgot-password')}
            variant="body2"
            sx={{ cursor: 'pointer' }}
          >
            Forgot your password?
          </Link>
        </Box>
      </Box>
    </Box>
  );
};